import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { applications, instances, products } from '../api';
import type { ProductConfigField } from '../types';
import ProduktKonfigFelder, { fehlendePflicht, startWerte } from './ProduktKonfigFelder';

/**
 * Einstellungsfelder an einer bestehenden Instanz bearbeiten — die ihrer
 * Application (Stufe 2), bei älteren Instanzen ohne Application die des Produkts.
 * Gespeichert wird in `currentConfig` (zusammengeführt, nicht ersetzt); wirksam
 * wird es beim nächsten Ausrollen.
 *
 * Eigene Komponente mit eigenen Hooks, nicht in der Detailseite: dort stehen
 * frühe `return`s, und Hooks dahinter ergeben eine weiße Seite.
 */
export default function ProduktKonfigKarte({ instanceId, productId, applicationId, config }: { instanceId: string; productId: string; applicationId?: string | null; config: Record<string, unknown> }) {
  const qc = useQueryClient();
  const { data: quelle } = useQuery({
    queryKey: applicationId ? ['applications', applicationId] : ['products', productId, 'detail'],
    queryFn: async (): Promise<{ configFields?: ProductConfigField[] }> =>
      (applicationId ? applications.get(applicationId) : products.get(productId)) as Promise<{ configFields?: ProductConfigField[] }>,
  });
  const felder = quelle?.configFields ?? [];
  const [werte, setWerte] = useState<Record<string, string>>({});
  useEffect(() => setWerte(startWerte(felder, config)), [felder.length, JSON.stringify(config)]); // eslint-disable-line react-hooks/exhaustive-deps

  const speichern = useMutation({
    mutationFn: () => instances.update(instanceId, { currentConfig: { ...config, ...werte } } as never),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['instances', instanceId] });
      qc.invalidateQueries({ queryKey: ['instances'] });
    },
  });

  if (!felder.some((f) => f.visibleInPC)) return null;
  const fehlt = fehlendePflicht(felder, werte);
  return (
    <div className="rounded-xl border border-slate-200 bg-surface p-5">
      <h3 className="mb-1 text-sm font-semibold text-slate-700">Einstellungen des Produkts</h3>
      <p className="mb-3 text-xs text-slate-500">Wirken beim nächsten Ausrollen.</p>
      <ProduktKonfigFelder felder={felder} werte={werte} setWerte={setWerte} />
      <div className="mt-3 flex items-center gap-3">
        <button
          onClick={() => speichern.mutate()}
          disabled={speichern.isPending || fehlt.length > 0}
          className="rounded-lg bg-brand-600 px-3 py-1.5 text-sm text-white hover:bg-brand-700 disabled:opacity-40"
        >
          {speichern.isPending ? 'Speichert …' : 'Speichern'}
        </button>
        {speichern.isSuccess && <span className="text-xs text-emerald-700">Gespeichert — beim nächsten Ausrollen wirksam.</span>}
        {fehlt.length > 0 && <span className="text-xs text-amber-700">Noch offen: {fehlt.join(', ')}</span>}
        {speichern.error && <span className="text-xs text-red-700">{(speichern.error as Error).message}</span>}
      </div>
    </div>
  );
}
